import type { CSSProperties } from 'react';
import { cn } from '@scio3/ui';

/**
 * The abstract, animated dashboard that plays on the TV inside the pre-hook modal.
 *
 * Shapes only — no numbers, no labels, no app branding. It stands in for "your
 * dashboard, on a screen" for whichever app the visitor came for, so nothing here
 * may ever become Power-BI-specific.
 *
 * Timings, colours and the reduced-motion kill switch all live in `../prehook.css`;
 * this file only supplies the stagger. Every element's RESTING markup is its FINISHED
 * state (bars at full height, the ring at offset 34, the trend line at offset 0), so
 * dropping `animation` there leaves a complete dashboard rather than an empty frame.
 */

type PrehookStyle = CSSProperties & Partial<Record<`--prehook-${string}`, string>>;

function inAt(delaySeconds: number): PrehookStyle {
  return { '--prehook-delay': `${delaySeconds}s` };
}

interface BarSpec {
  /** Height as a share of the card's plot area. */
  height: string;
  tint: 1 | 2 | 3 | 4;
  /** Entrance stagger, seconds. */
  delay: number;
  /** Seconds. Deliberately uneven so the six bars never pulse in unison. */
  breathe: [duration: number, delay: number];
}

const BARS_TOP: BarSpec[] = [
  { height: '42%', tint: 1, delay: 0.28, breathe: [6.4, 1.6] },
  { height: '68%', tint: 3, delay: 0.36, breathe: [5.6, 2.1] },
  { height: '34%', tint: 1, delay: 0.44, breathe: [7.1, 1.9] },
  { height: '86%', tint: 4, delay: 0.52, breathe: [6.1, 2.6] },
  { height: '52%', tint: 1, delay: 0.6, breathe: [5.9, 3.1] },
  { height: '96%', tint: 3, delay: 0.68, breathe: [6.8, 2.3] },
];

const BARS_BOTTOM: BarSpec[] = [
  { height: '38%', tint: 1, delay: 0.4, breathe: [6.6, 2.4] },
  { height: '62%', tint: 3, delay: 0.48, breathe: [5.4, 1.8] },
  { height: '46%', tint: 1, delay: 0.56, breathe: [7.2, 2.9] },
  { height: '82%', tint: 3, delay: 0.64, breathe: [6.0, 2.2] },
  { height: '56%', tint: 1, delay: 0.72, breathe: [6.9, 3.3] },
  { height: '94%', tint: 4, delay: 0.8, breathe: [5.8, 1.5] },
];

const CARD_BASE = 'prehook-card rounded-lg px-[18px] py-4';

function Bar({ spec }: { spec: BarSpec }) {
  const style: PrehookStyle = {
    height: spec.height,
    '--prehook-delay': `${spec.delay}s`,
    '--prehook-breathe-duration': `${spec.breathe[0]}s`,
    '--prehook-breathe-delay': `${spec.breathe[1]}s`,
  };
  return (
    <div
      aria-hidden
      className={cn('prehook-bar w-[15px] rounded-sm', `prehook-fill-${spec.tint}`)}
      style={style}
    />
  );
}

/** A text-like rule that draws in from the left. */
function Line({
  width,
  tint,
  delay,
  className,
}: {
  width: string;
  tint: 1 | 2 | 3 | 4 | 'track';
  delay: number;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn('prehook-line h-2 rounded-sm', `prehook-fill-${tint}`, className)}
      style={{ width, ...inAt(delay) }}
    />
  );
}

export function PrehookDashboard() {
  return (
    <div
      aria-hidden
      className="grid h-full w-full grid-cols-6 grid-rows-2 gap-2.5 p-3"
      data-testid="app-intent-prehook-dashboard"
    >
      <div className={cn(CARD_BASE, 'col-span-2 flex flex-col gap-[9px]')} style={inAt(0.04)}>
        <div
          className="prehook-pop prehook-fill-1 h-[34px] w-[34px] rounded-lg"
          style={inAt(0.16)}
        />
        <Line width="76%" tint={2} delay={0.24} className="mt-0.5" />
        <Line width="48%" tint={1} delay={0.32} />
      </div>

      <div
        className={cn(CARD_BASE, 'col-span-2 flex items-end justify-between gap-1.5')}
        style={inAt(0.1)}
      >
        {BARS_TOP.map((spec) => (
          <Bar key={spec.delay} spec={spec} />
        ))}
      </div>

      <div className={cn(CARD_BASE, 'col-span-2 flex items-center gap-3.5')} style={inAt(0.16)}>
        <svg viewBox="0 0 52 52" className="block h-[52px] w-[52px] flex-none -rotate-90">
          <circle
            cx="26"
            cy="26"
            r="22"
            fill="none"
            stroke="var(--prehook-track)"
            strokeWidth="7"
          />
          {/* Rests at the arc's END offset so reduced motion shows a filled ring. */}
          <circle
            className="prehook-ring"
            cx="26"
            cy="26"
            r="22"
            fill="none"
            stroke="var(--prehook-tint-3)"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray="138"
            strokeDashoffset="34"
          />
        </svg>
        <div className="flex flex-1 flex-col gap-[9px]">
          <Line width="86%" tint={2} delay={0.44} />
          <Line width="54%" tint={1} delay={0.52} />
        </div>
      </div>

      <div className={cn(CARD_BASE, 'col-span-2 flex flex-col pb-3.5')} style={inAt(0.22)}>
        <div className="flex min-h-0 flex-1 items-end justify-between gap-1.5">
          {BARS_BOTTOM.map((spec) => (
            <Bar key={spec.delay} spec={spec} />
          ))}
        </div>
        <div
          aria-hidden
          className="prehook-line prehook-fill-track mt-2.5 h-[3px] flex-none rounded-xs"
          style={inAt(0.3)}
        />
      </div>

      <div className={cn(CARD_BASE, 'col-span-2 flex flex-col pb-3.5')} style={inAt(0.28)}>
        <Line width="40%" tint={2} delay={0.46} className="flex-none" />
        <svg
          viewBox="0 0 160 60"
          preserveAspectRatio="none"
          className="mt-2.5 block h-auto min-h-0 w-full flex-1"
        >
          <path
            className="prehook-fade"
            d="M4 46 C24 46 30 22 50 20 C70 18 76 38 96 34 C116 30 124 10 156 16 L156 56 L4 56 Z"
            fill="rgb(127 217 198 / .3)"
          />
          {/* Rests fully drawn (offset 0) so reduced motion shows the whole line. */}
          <path
            className="prehook-draw"
            d="M4 46 C24 46 30 22 50 20 C70 18 76 38 96 34 C116 30 124 10 156 16"
            fill="none"
            stroke="var(--prehook-tint-3)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="300"
            strokeDashoffset="0"
          />
        </svg>
      </div>

      <div className={cn(CARD_BASE, 'col-span-2 flex flex-col gap-[9px]')} style={inAt(0.34)}>
        <Line width="80%" tint={2} delay={0.54} />
        <Line width="58%" tint={1} delay={0.62} />
        <div className="mt-1 flex gap-2">
          <div
            aria-hidden
            className="prehook-pop prehook-fill-1 h-[18px] w-[46px] rounded-full"
            style={inAt(0.72)}
          />
          <div
            aria-hidden
            className="prehook-pop prehook-fill-2 h-[18px] w-[34px] rounded-full"
            style={inAt(0.8)}
          />
        </div>
      </div>
    </div>
  );
}
